import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Chip, Button,
  List, ListItem, ListItemText, Collapse, Alert, CircularProgress,
  useTheme, useMediaQuery, Avatar, Divider, IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
  Psychology as PsychologyIcon, Assessment as AssessmentIcon,
  Flag as TargetIcon, Notes as NotesIcon, Dashboard as DashboardIcon,  // ← Change this line
  SmartToy as AIIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { FaTrophy, FaExclamationTriangle, FaCheckCircle, FaClock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import EnhancedAITradingChat from './EnhancedAITradingChat';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [trades, setTrades] = useState([]);
  const [psychologyAnalysis, setPsychologyAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [aiChatOpen, setAiChatOpen] = useState(false);
  
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileData();
  }, []);

 const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Please log in to view your profile.');
        setLoading(false);
        return;
      }

      // Fetch data with better error handling
      const [tradesResponse, psychologyResponse] = await Promise.allSettled([
        api.get('/api/trades'),
        api.get('/api/psychology/analysis')
      ]);

      // Handle trades response
      if (tradesResponse.status === 'fulfilled') {
        setTrades(tradesResponse.value.data || []);
      } else {
        console.error('Failed to fetch trades:', tradesResponse.reason);
        setTrades([]);
      }

      // Handle psychology response
      if (psychologyResponse.status === 'fulfilled') {
        setPsychologyAnalysis(psychologyResponse.value.data);
      } else {
        console.error('Failed to fetch psychology analysis:', psychologyResponse.reason);
        setPsychologyAnalysis(null);
      }

      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user')) || {
        email: 'User',
        initialBalance: 5000,
        profitTarget: 0
      };

      setUser(userData);
      
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const stats = {
    totalTrades: trades.length,
    winRate: trades.length > 0 ? (trades.filter(t => t.pnl > 0).length / trades.length) * 100 : 0,
    totalPnL: trades.reduce((sum, t) => sum + (t.pnl || 0), 0),
    profitFactor: trades.filter(t => t.pnl < 0).length > 0 ? 
      Math.abs(trades.filter(t => t.pnl > 0).reduce((sum, t) => sum + (t.pnl || 0), 0) / 
      trades.filter(t => t.pnl < 0).reduce((sum, t) => sum + Math.abs(t.pnl || 0), 0)) : 0,
    performance: trades.length > 0 ? 
      (trades.filter(t => t.pnl > 0).length / trades.length > 0.5 ? 'Good' : 'Needs Improvement') : 'No Data'
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper elevation={0} sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 4, 
        borderRadius: 0,
        mb: 3
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'white', color: 'primary.main' }}>
              <Typography variant="h4">{user?.email?.charAt(0).toUpperCase()}</Typography>
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {user?.email || 'Trader'}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Trading Profile & Analytics
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {stats.totalTrades >= 20 && (
          <Paper elevation={2} sx={{ 
            mb: 4, 
            p: 3, 
            bgcolor: 'success.light', 
            borderRadius: 2, 
            color: 'white',
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 1,
              mb: 2
            }}>
              <FaTrophy />
              Trading in the Zone Achievement
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Congratulations! You've completed {stats.totalTrades} trades and are now "Trading in the Zone"
              as described in Mark Douglas's book. You've developed the psychological foundation for consistent trading success.
            </Typography>
            <Chip
              label={`${stats.totalTrades} Trades Completed`}
              color="success"
              sx={{ color: 'white', bgcolor: 'success.dark' }}
            />
          </Paper>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
              }}
              onClick={() => handleNavigation('/dashboard')}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  {stats.totalTrades}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Trades
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
              }}
              onClick={() => handleNavigation('/dashboard')}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h4" color="secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  {stats.winRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Win Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
              }}
              onClick={() => handleNavigation('/dashboard')}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h4" color={stats.totalPnL >= 0 ? 'success' : 'error'} sx={{ fontWeight: 600, mb: 1 }}>
                  ${stats.totalPnL.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total P&L
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={2} 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
              }}
              onClick={() => handleNavigation('/dashboard')}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h4" color="info" sx={{ fontWeight: 600, mb: 1 }}>
                  {stats.profitFactor.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Profit Factor
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
 {psychologyAnalysis && (
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              cursor: 'pointer'
            }} onClick={() => toggleSection('psychology')}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PsychologyIcon color="primary" />
                Psychology Analysis
              </Typography>
              {expandedSections.psychology ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            
            <Collapse in={expandedSections.psychology}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h3" color="primary" sx={{ fontWeight: 600 }}>
                      {psychologyAnalysis.psychologyScore}/100
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Psychology Score
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Chip 
                      label={psychologyAnalysis.riskLevel} 
                      color={psychologyAnalysis.riskLevel === 'LOW' ? 'success' : 
                             psychologyAnalysis.riskLevel === 'MEDIUM' ? 'warning' : 'error'}
                      variant="outlined"
                      size="large"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Risk Level
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {psychologyAnalysis.warnings && psychologyAnalysis.warnings.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaExclamationTriangle color="orange" />
                    Warnings
                  </Typography>
                  <List dense>
                    {psychologyAnalysis.warnings.map((warning, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={warning} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              {psychologyAnalysis.recommendations && psychologyAnalysis.recommendations.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaCheckCircle color="green" />
                    Recommendations
                  </Typography>
                  <List dense>
                    {psychologyAnalysis.recommendations.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Collapse>
          </Paper>
        )}

        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Quick Actions
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<TargetIcon />}
                onClick={() => handleNavigation('/dashboard')}
                sx={{ height: 56 }}
              >
                Set Targets
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<NotesIcon />}
                onClick={() => handleNavigation('/Journal')}
                sx={{ height: 56 }}
              >
                View Notes
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<DashboardIcon />}
                onClick={() => handleNavigation('/dashboard')}
                sx={{ height: 56 }}
              >
                Dashboard
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AIIcon />}
                onClick={() => setAiChatOpen(true)}
                sx={{ height: 56 }}
              >
                AI Chat
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2,
            cursor: 'pointer'
          }} onClick={() => toggleSection('history')}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FaClock color="primary" />
              Recent Trading History
            </Typography>
            {expandedSections.history ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          
          <Collapse in={expandedSections.history}>
            {trades.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No trading history available. Start trading to see your history here.
              </Typography>
            ) : (
              <List dense>
                {trades.slice(0, 5).map((trade, index) => (
                  <ListItem 
                    key={trade._id || index}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => handleNavigation('/dashboard')}
                  >
                    <ListItemText
                      primary={`${trade.symbol} - ${trade.side}`}
                      secondary={`${new Date(trade.openDate).toLocaleDateString()} | P&L: $${trade.pnl?.toFixed(2) || '0.00'}`}
                    />
                    <Chip
                      label={trade.pnl >= 0 ? 'Profit' : 'Loss'}
                      color={trade.pnl >= 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Collapse>
        </Paper>
      </Box>

      <EnhancedAITradingChat
        user={user}
        isOpen={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        userPerformance={{
          overall: stats.performance,
          riskLevel: psychologyAnalysis?.riskLevel,
          psychologyScore: psychologyAnalysis?.psychologyScore,
          totalTrades: stats.totalTrades,
          winRate: stats.winRate,
          profitFactor: stats.profitFactor,
          avgHoldingTime: '2 days'
        }}
      />
    </Box>
  );
};

export default Profile;
